import axios from 'axios'
import Cookies from 'js-cookie'

import AbstractService from '../../abstractService'

/**
 * @class JwtService
 */
export default class JwtService extends AbstractService {

    /**
     * User login
     *
     * @param args
     *
     * @returns {Promise<AxiosResponse<T>>}
     */
    login(...args) {

        return axios.post(this.getUrl(this.config.loginEndpoint), ...args)
    }

    /**
     * Verify auto login hash
     * 
     * @param {string} hash - Auto login hash
     * @returns {Promise<AxiosResponse<T>>}
     */
    verifyAutoLoginHash(hash) {
        return axios.post(this.getUrl(`/auth/verify-ah-auto-login/${hash}`))
    }

    /**
     * User registration
     *
     * @param args
     *
     * @returns {Promise<AxiosResponse<T>>}
     */
    register(...args) {

        return axios.post(this.getUrl(this.config.registerEndpoint), ...args)
    }

    /**
     * Forgot password request
     *
     * @param args
     *
     * @returns {Promise<AxiosResponse<T>>}
     */
    requestPassword(...args) {

        return axios.post(this.getUrl(this.config.requestPasswordEndpoint), ...args)
    }

    /**
     * Reset password request
     *
     * @param args
     *
     * @returns {Promise<AxiosResponse<T>>}
     */
    resetPassword(...args) {

        return axios.put(this.getUrl(this.config.setPasswordEndpoint), ...args)
    }

    /**
     * Logout request
     *
     * @returns {Promise<AxiosResponse<T>>}
     */
    logout() {

        return axios.delete(this.getUrl(this.config.logoutEndpoint))
    }

    /**
     * Set access token
     *
     * @param {string} value
     * @param {boolean} remember
     */
    setToken(value, remember = false) {

        const options = {}

        if (remember) {

            options['expires'] = 7
        }

        try {
            console.log('[JWT_SERVICE] Setting token, length:', value.length, 'bytes')
            
            Cookies.set(this.config.storageTokenKeyName, value, {
                SameSite: 'lax',
                ...options
            })

            console.log('[JWT_SERVICE] Token cookie set successfully')
        } catch (error) {
            console.error('[JWT_SERVICE] Error setting token cookie:', error)
        }
    }

    /**
     * Remove access token
     */
    removeToken() {

        Cookies.remove(this.config.storageTokenKeyName)
    }

    /**
     * Get user data
     *
     * @returns {object}
     */
    getUserData() {

        // Read from localStorage since userData exceeds cookie size limit
        const userData = localStorage.getItem(this.config.storageUserDataKeyName)

        return userData ? JSON.parse(userData) : null
    }

    /**
     * Set user data
     *
     * @param {object} userData
     * @param {boolean} remember
     */
    setUserData(userData, remember = false) {
        try {
            const serialized = JSON.stringify(userData)
            const size = new Blob([serialized]).size
            console.log('[JWT_SERVICE] userData size:', size, 'bytes')

            // Use localStorage for userData since it exceeds cookie size limit (4KB)
            // localStorage has 5-10MB limit which is sufficient
            localStorage.setItem(this.config.storageUserDataKeyName, serialized)
            console.log('[JWT_SERVICE] userData stored in localStorage successfully')
        } catch (error) {
            console.error('[JWT_SERVICE] Error storing userData:', error)
        }
    }

    /**
     * Get company data
     * @returns {Record<string, any>}
     */
    getSubscriptionData() {
        const data = Cookies.get(this.config.storageSubscriptionKeyData)

        return data ? JSON.parse(data) : null
    }

    /**
     * Set company data
     * @param {Record<string, any>} data 
     */
    setSubscriptionData(data) {

        Cookies.set(this.config.storageSubscriptionKeyData, JSON.stringify(data), {
            SameSite: 'lax',
            expires: 7
        })
    }

    /**
     * Remove company data
     */
    removeSubscriptionData() {
        Cookies.remove(this.config.storageSubscriptionKeyData)
    }

    /**
     * Get company balance data
     * @returns {Record<string, any>}
     */
    getBalanceData() {
        const data = Cookies.get(this.config.storageBalanceKeyData)

        return data ? JSON.parse(data) : null
    }

    /**
     * Set company balance data
     * @param {Record<string, any>} data
     */
    setBalanceData(data) {

        Cookies.set(this.config.storageBalanceKeyData, JSON.stringify(data), {
            SameSite: 'lax',
            expires: 7
        })
    }

    /**
     * Remove company balance data
     */
    removeBalanceData() {
        Cookies.remove(this.config.storageBalanceKeyData)
    }

    /**
     * Get company payout data
     * @returns {Record<string, any>}
     */
    getPayoutData() {
        const data = Cookies.get(this.config.storagePayoutData)

        return data ? JSON.parse(data) : null
    }

    /**
     * Set company payout data
     * @param {Record<string, any>} data
     */
    setPayoutData(data) {

        Cookies.set(this.config.storagePayoutData, JSON.stringify(data), {
            SameSite: 'lax',
            expires: 7
        })
    }

    /**
     * Remove company payout data
     */
    removePayoutData() {
        Cookies.remove(this.config.storagePayoutData)
    }


    /**
     * Remove user data
     * Remove user data
     */
    removeUserData() {

        localStorage.removeItem(this.config.storageUserDataKeyName)
    }
}
